/**
 * QrScannerModal — POS-style barcode/QR scanner.
 *
 * Two input paths (both call `onScan(shortId, product)`):
 *   1. CAMERA — `html5-qrcode` decodes the live video feed. Works on mobile +
 *      any desktop with a webcam. Continuous mode: keeps scanning until closed.
 *   2. KEYBOARD — most hardware USB/Bluetooth scanners act as HID keyboards.
 *      A focused text input catches their fast `type-then-Enter` sequence
 *      automatically. Works even with no camera.
 *
 * Scanned value is the QR URL (e.g. `https://app.opikjewels.com/product/AB12CDEF`)
 * or just the raw short_id (`AB12CDEF`). We extract the short_id, hit
 * `GET /api/lookup/by-qr/{id}` to resolve, and call back with both.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { X, Camera, Keyboard, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// Extract a scan token from a freshly-scanned string. Accepts THREE formats
// so any sticker printed in the past still resolves at the POS scanner:
//   1. New `https://app.opikjewels.com/product/{short_id}` URLs
//   2. Old `https://opikjewels.com/{product-uuid}` URLs (legacy sticker batch)
//   3. Plain short_id, plain UUID, or hand-typed SKU like `OJ-00040`
// The token is passed through to /api/lookup/by-qr/{token} which itself
// tries qr_short_id → product.id → sku in sequence.
const _UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function extractShortId(raw) {
  if (!raw) return "";
  const trimmed = raw.trim();
  // New URL pattern — e.g. https://app.opikjewels.com/product/AB12CDEF
  const m = trimmed.match(/\/product\/([A-Z0-9]{4,16})/i);
  if (m) return m[1].toUpperCase();
  // Legacy URL pattern — older sticker batches encoded the full product UUID
  // directly after the domain, e.g. https://opikjewels.com/{uuid}
  const u = trimmed.match(_UUID_RE);
  if (u) return u[0].toLowerCase();
  // Otherwise treat the trimmed value as a short_id or SKU if it looks like one.
  if (/^[A-Z0-9-]{4,32}$/i.test(trimmed)) return trimmed.toUpperCase();
  return trimmed;  // pass through — backend will 404 if unknown
}

export default function QrScannerModal({ onClose, onScan, title = "Scan to add" }) {
  const [mode, setMode] = useState("camera");  // camera | keyboard
  const [manualValue, setManualValue] = useState("");
  const [recent, setRecent] = useState([]);  // [{sku, name, ts, ok}]
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const manualInputRef = useRef(null);
  const scannerRef = useRef(null);
  const containerId = "qr-scanner-region";
  // Debounce: after a successful scan, ignore further detections for 1.5s so
  // the camera doesn't fire the same code 30× before the operator removes it.
  const lockRef = useRef(0);

  const resolveAndCallback = useCallback(async (raw) => {
    const short = extractShortId(raw);
    if (!short) {
      toast.error("Empty scan");
      return;
    }
    if (Date.now() < lockRef.current) return;  // debounce
    lockRef.current = Date.now() + 1500;
    try {
      const { data } = await api.get(`/lookup/by-qr/${encodeURIComponent(short)}`);
      // Beep + visual feedback
      try {
        const a = new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=");
        a.play().catch(() => {});
      } catch (_) { /* no-audio context */ }
      setRecent((r) => [{ sku: data.sku, name: data.name, ts: Date.now(), ok: true }, ...r].slice(0, 5));
      onScan(short, data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Lookup failed";
      toast.error(msg);
      setRecent((r) => [{ sku: short, name: msg, ts: Date.now(), ok: false }, ...r].slice(0, 5));
    }
  }, [onScan]);

  // Camera scanner lifecycle —
  // STRATEGY:
  //   1. If the browser exposes the native BarcodeDetector API
  //      (Safari 17+ on iPhone, Chrome on Android), bypass html5-qrcode
  //      entirely and run our own getUserMedia + rAF loop. This is the
  //      ONLY reliable path on iPhone 15/15 Pro because the html5-qrcode
  //      wrapper hits a Safari WebRTC quirk that drops frames before
  //      decode even starts. Native API → ~30 fps decode + autofocus.
  //   2. Otherwise (older Safari, Firefox), fall back to html5-qrcode.
  useEffect(() => {
    if (mode !== "camera") return;
    let cancelled = false;
    let stream = null;
    let rafId = null;
    let scanner = null;
    const videoEl = document.createElement("video");
    videoEl.setAttribute("playsinline", "true");   // critical for iOS Safari
    videoEl.setAttribute("muted", "true");
    videoEl.muted = true;
    videoEl.style.width = "100%";
    videoEl.style.height = "100%";
    videoEl.style.objectFit = "cover";

    const useNative = typeof window !== "undefined" && "BarcodeDetector" in window;

    const startNative = async () => {
      try {
        // Constraints tuned for iPhone 15 Safari:
        //   - environment camera (back)
        //   - continuous autofocus so close-range tags stay sharp
        //   - 1280×720 ideal (Safari often gives ~720 native back camera)
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            focusMode: { ideal: "continuous" },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const region = document.getElementById(containerId);
        if (region) {
          region.innerHTML = "";
          region.appendChild(videoEl);
        }
        videoEl.srcObject = stream;
        await videoEl.play();
        // Try to nudge focus to continuous on iPhones that report capability
        try {
          const track = stream.getVideoTracks()[0];
          const caps = track.getCapabilities ? track.getCapabilities() : {};
          if (caps.focusMode && Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
            await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
          }
        } catch { /* ignore — best effort */ }

        const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        if (!cancelled) setCameraReady(true);

        const loop = async () => {
          if (cancelled) return;
          try {
            const codes = await detector.detect(videoEl);
            if (codes && codes.length > 0) {
              const raw = codes[0].rawValue || codes[0].cornerPoints || "";
              if (raw) resolveAndCallback(String(raw));
            }
          } catch { /* per-frame failure is fine */ }
          rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
      } catch (e) {
        console.error("Native scanner init failed:", e);
        if (!cancelled) {
          setCameraError(e?.message || "Could not start camera");
          // Don't auto-switch to keyboard here — user might just need to grant permission.
        }
      }
    };

    const startFallback = async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
        if (cancelled) return;
        scanner = new Html5Qrcode(containerId, {
          verbose: false,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
          ],
        });
        scannerRef.current = scanner;
        const qrboxFn = (vw, vh) => {
          const min = Math.min(vw, vh);
          const size = Math.max(180, Math.floor(min * 0.7));
          return { width: size, height: size };
        };
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 15,
            qrbox: qrboxFn,
            aspectRatio: 1.0,
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
            disableFlip: false,
          },
          (decodedText) => { resolveAndCallback(decodedText); },
          () => { /* per-frame decode failures ignored */ },
        );
        if (!cancelled) setCameraReady(true);
      } catch (e) {
        console.error("Fallback scanner init failed:", e);
        if (!cancelled) {
          setCameraError(e?.message || "Could not start camera");
          setMode("keyboard");
        }
      }
    };

    if (useNative) startNative(); else startFallback();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (stream) {
        stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
      }
      if (scanner) {
        try {
          const state = typeof scanner.getState === "function" ? scanner.getState() : null;
          if (state === 2 || state === 3) {
            scanner.stop().then(() => scanner.clear()).catch(() => {});
          } else {
            try { scanner.clear(); } catch { /* noop */ }
          }
        } catch { /* swallow */ }
      }
    };
  }, [mode, resolveAndCallback]);

  // Auto-focus manual input when in keyboard mode (catches hardware scanner input)
  useEffect(() => {
    if (mode === "keyboard" && manualInputRef.current) {
      manualInputRef.current.focus();
    }
  }, [mode]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualValue.trim()) return;
    resolveAndCallback(manualValue);
    setManualValue("");
    // refocus so hardware scanner can stream more codes
    setTimeout(() => manualInputRef.current?.focus(), 50);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="qr-scanner-modal">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-lg fade-up">
        {/* Header */}
        <div className="border-b border-white/5 px-5 sm:px-6 py-4 flex justify-between items-center">
          <div>
            <div className="overline text-[#D4AF37]">POS Scanner</div>
            <h3 className="serif text-lg sm:text-xl mt-1">{title}</h3>
          </div>
          <button onClick={onClose} data-testid="qr-scanner-close">
            <X className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="px-5 sm:px-6 pt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMode("camera")}
            data-testid="scanner-mode-camera"
            className={`flex items-center justify-center gap-2 py-2.5 text-[0.65rem] uppercase tracking-wider border transition-all ${mode === "camera" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#8A8A8A]"}`}
          >
            <Camera className="w-3.5 h-3.5" strokeWidth={1.5} /> Camera
          </button>
          <button
            type="button"
            onClick={() => setMode("keyboard")}
            data-testid="scanner-mode-keyboard"
            className={`flex items-center justify-center gap-2 py-2.5 text-[0.65rem] uppercase tracking-wider border transition-all ${mode === "keyboard" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#8A8A8A]"}`}
          >
            <Keyboard className="w-3.5 h-3.5" strokeWidth={1.5} /> Hardware / Manual
          </button>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-5">
          {mode === "camera" ? (
            <div>
              <div
                id={containerId}
                className="w-full bg-black border border-white/5 overflow-hidden"
                style={{ aspectRatio: "1 / 1", minHeight: "240px" }}
                data-testid="scanner-camera-region"
              />
              <div className="text-[0.65rem] text-[#8A8A8A] mt-3 text-center leading-relaxed">
                {cameraReady ? (
                  <>
                    Hold the QR <span className="text-[#D4AF37]">~20-30 cm</span> away.
                    {" "}If the image is blurry it won&apos;t scan — pull back slightly.<br/>
                    Continuous — adds while modal is open.
                  </>
                ) : cameraError ? (
                  <span className="text-red-400">Camera unavailable: {cameraError}. Use Hardware/Manual instead.</span>
                ) : (
                  <>Waiting for camera permission…</>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleManualSubmit}>
              <label className="overline mb-2 block">Scan code or SKU</label>
              <input
                ref={manualInputRef}
                type="text"
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                placeholder="Scanner types here, or paste short ID"
                className="luxe-input w-full px-4 py-3 text-base mono"
                autoFocus
                autoComplete="off"
                data-testid="scanner-manual-input"
              />
              <div className="text-[0.65rem] text-[#8A8A8A] mt-2 leading-relaxed">
                Hardware USB/Bluetooth scanners send keystrokes — focus this field and trigger the scan. They auto-press Enter for you.
              </div>
              <button
                type="submit"
                className="btn-gold w-full mt-3 py-2.5 text-xs uppercase tracking-wider"
                data-testid="scanner-manual-submit"
              >
                Add to sale
              </button>
            </form>
          )}

          {/* Recent scans log */}
          {recent.length > 0 && (
            <div className="mt-5 border-t border-white/5 pt-4">
              <div className="overline text-[#7a7a7a] mb-2">Recent scans</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {recent.map((r, i) => (
                  <div key={`${r.ts}-${i}`} className="flex items-center gap-2 text-[0.7rem]">
                    {r.ok ? (
                      <CheckCircle2 className="w-3 h-3 text-[#7BA585] shrink-0" strokeWidth={1.5} />
                    ) : (
                      <AlertCircle className="w-3 h-3 text-red-400 shrink-0" strokeWidth={1.5} />
                    )}
                    <span className="mono text-[#D4AF37]">{r.sku}</span>
                    <span className="text-[#8A8A8A] truncate">· {r.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/5 px-5 sm:px-6 py-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="btn-outline-gold px-5 py-2 text-xs uppercase tracking-wider"
            data-testid="scanner-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
